/* CG Lazy Loading v3.0 - Hardened IntersectionObserver Implementation */
(function() {
  'use strict';
  
  // Prevent duplicate initialization
  if (window.__cgLazyLoadInitialized) return;
  window.__cgLazyLoadInitialized = true;
  
  const config = {
    rootMargin: '200px 0px', // Start loading 200px before visible
    threshold: 0.01,
    fallbackDelay: 100 // ms delay for non-IO fallback
  };
  
  /**
   * Load a single image with error handling
   */
  function loadImage(img) {
    // Skip if already loaded
    if (img.classList.contains('loaded') || img.classList.contains('cg-loading')) return;
    
    img.classList.add('cg-loading');
    
    // Create temporary image to preload
    const tempImg = new Image();
    
    tempImg.onload = function() {
      // Successfully loaded - apply to actual img
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      
      // Remove loading state, add loaded state
      img.classList.remove('cg-loading', 'cg-lazy');
      img.classList.add('loaded');
      
      // Ensure visible (remove any hiding styles)
      img.style.opacity = '1';
      img.style.visibility = 'visible';
      
      // Clean up data attributes
      delete img.dataset.src;
      delete img.dataset.srcset;
    };
    
    tempImg.onerror = function() {
      // Error loading - show fallback or placeholder
      console.warn('CG Lazy Load: Failed to load image', img.dataset.src);
      img.classList.remove('cg-loading');
      img.classList.add('cg-load-error');
      
      // Still make visible (show broken state rather than hidden)
      img.style.opacity = '1';
      img.style.visibility = 'visible';
      
      // Try original src if available
      if (img.dataset.src && img.src !== img.dataset.src) {
        img.src = img.dataset.src;
      }
    };
    
    // Start loading
    tempImg.src = img.dataset.src || img.dataset.srcset?.split(' ')[0] || '';
  }
  
  /**
   * Initialize IntersectionObserver-based lazy loading
   */
  function initObserverLazyLoad() {
    const imageObserver = new IntersectionObserver(function(entries, observer) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          loadImage(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: config.rootMargin,
      threshold: config.threshold
    });
    
    // Observe all lazy images
    function observeLazyImages() {
      document.querySelectorAll('.cg-lazy[data-src]:not(.loaded):not(.cg-loading)').forEach(function(img) {
        imageObserver.observe(img);
      });
    }
    
    observeLazyImages();
    
    // Re-observe on dynamic content additions
    const mutationObserver = new MutationObserver(function(mutations) {
      let hasNewImages = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('cg-lazy') && node.dataset.src) {
              imageObserver.observe(node);
              hasNewImages = true;
            }
            const lazyImages = node.querySelectorAll && node.querySelectorAll('.cg-lazy[data-src]:not(.loaded)');
            if (lazyImages && lazyImages.length) {
              lazyImages.forEach(function(img) {
                imageObserver.observe(img);
              });
              hasNewImages = true;
            }
          }
        });
      });
    });
    
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    
    console.log('✅ CG Lazy Loading v3.0 (IntersectionObserver) initialized');
  }
  
  /**
   * Fallback for browsers without IntersectionObserver
   */
  function initFallbackLazyLoad() {
    console.log('⚠️ IntersectionObserver not supported, using fallback');
    
    function loadVisibleImages() {
      const lazyImages = document.querySelectorAll('.cg-lazy[data-src]:not(.loaded)');
      const viewportHeight = window.innerHeight;
      
      lazyImages.forEach(function(img) {
        const rect = img.getBoundingClientRect();
        // Load if within viewport + buffer
        if (rect.top < viewportHeight + 300 && rect.bottom > -300) {
          loadImage(img);
        }
      });
    }
    
    // Initial load
    loadVisibleImages();
    
    // On scroll/resize
    let throttleTimer;
    function throttledLoad() {
      if (throttleTimer) return;
      throttleTimer = setTimeout(function() {
        loadVisibleImages();
        throttleTimer = null;
      }, config.fallbackDelay);
    }
    
    window.addEventListener('scroll', throttledLoad, { passive: true });
    window.addEventListener('resize', throttledLoad, { passive: true });
  }
  
  /**
   * Immediate load for above-the-fold images
   */
  function loadAboveFold() {
    // Load any lazy images that are immediately visible
    document.querySelectorAll('.cg-lazy[data-src]').forEach(function(img) {
      const rect = img.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        loadImage(img);
      }
    });
  }
  
  /**
   * Main initialization
   */
  function init() {
    // Load above-fold images immediately
    loadAboveFold();
    
    // Use IntersectionObserver if available, fallback otherwise
    if ('IntersectionObserver' in window) {
      initObserverLazyLoad();
    } else {
      initFallbackLazyLoad();
    }
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();
